// src/App.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { Navbar } from './components/Layout/Navbar';
import { BottomNavigation } from './components/BottomNavigation';
import { initDeliveryConfig } from './utils/deliveryConfig';
import { PWAInstallBanner } from './components/PWAInstallBanner';
import { usePWA } from './hooks/usePWA';
import { WelcomeDialog } from './components/WelcomeDialog';
import { searchQueue } from './services/searchQueue';
import { AliveScope, KeepAlive } from 'react-activation';
import { supabase } from './services/supabase';

import { Home } from './pages/Home';
import { Products } from './pages/Products';
import { ProductDetail } from './pages/ProductDetail';
import { Cart } from './pages/Cart';
import { Profile } from './pages/Profile';
import { ProfileEdit } from './pages/ProfileEdit';
import { CategoryPage } from './pages/CategoryPage';
import { Checkout } from './pages/Checkout';
import { DeliverySelection } from './pages/DeliverySelection';
import { Orders } from './pages/Orders';
import { BrowserInstructionModal } from "./components/BrowserInstructionModal";
import { Wishlist } from "./pages/Wishlist";
import { VisitorTracker } from "./components/VisitorTracker";
import { SearchPage } from './pages/SearchPage';
import { AuthCallback } from "./pages/AuthCallback";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ResetPassword } from "./pages/ResetPassword";
import {DebugPanel} from "./components/DebugPanel";

// ========== КОМПОНЕНТЫ ИНИЦИАЛИЗАЦИИ ==========

const ScrollRestorationSetup: React.FC = () => {
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    }, []);
    return null;
};

const DeliveryConfigInitializer: React.FC = () => {
    useEffect(() => {
        initDeliveryConfig().catch(console.error);
    }, []);
    return null;
};

const SearchQueueInitializer: React.FC = () => {
    useEffect(() => {
        searchQueue.init();
        console.log('🔍 SearchQueue инициализирован');
    }, []);
    return null;
};

const SessionRefresher: React.FC = () => {
    useEffect(() => {
        const handler = async () => {
            if (document.visibilityState === 'visible') {
                await supabase.auth.refreshSession().catch(() => {});
            }
        };
        document.addEventListener('visibilitychange', handler);
        return () => document.removeEventListener('visibilitychange', handler);
    }, []);
    return null;
};

// ========== УЛУЧШЕННЫЙ ЗАЩИЩЕННЫЙ МАРШРУТ ==========

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading, session } = useAuth();
    const [isVerifying, setIsVerifying] = useState(true);
    const location = useLocation();

    useEffect(() => {
        // В ProtectedRoute, измените verifyAuth
        const verifyAuth = async () => {
            if (loading) return;

            if (user) {
                // Небольшая задержка для плавного перехода
                setTimeout(() => setIsVerifying(false), 100);
                return;
            }

            if (session) {
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                if (currentSession?.user) {
                    setTimeout(() => setIsVerifying(false), 500);
                    return;
                }
            }

            sessionStorage.setItem('redirectAfterLogin', location.pathname);
            setIsVerifying(false);
        };

        verifyAuth();
    }, [user, loading, session, location.pathname]);

    // Показываем загрузку во время проверки
    if (loading || isVerifying) {
        return (
            <div className="loader-container">
                <div className="spinner" />
                <p>Проверка доступа...</p>
            </div>
        );
    }

    // Если нет пользователя - редирект на логин
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

// ========== КОМПОНЕНТ ОБЕРТКИ ДЛЯ ПУБЛИЧНЫХ МАРШРУТОВ ==========

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner" />
                <p>Загрузка...</p>
            </div>
        );
    }

    // Если пользователь уже авторизован и пытается зайти на страницу логина/регистрации
    if (user && (window.location.pathname === '/login' || window.location.pathname === '/register')) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

// ========== КОМПОНЕНТ-ОБЕРТКА ДЛЯ ОТЛОЖЕННОЙ ИНИЦИАЛИЗАЦИИ ==========

const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    if (!isInitialized) {
        return (
            <div className="loader-container">
                <div className="spinner" />
                <p>Инициализация...</p>
            </div>
        );
    }

    return <>{children}</>;
};

// ========== ОСНОВНЫЕ МАРШРУТЫ ПРИЛОЖЕНИЯ ==========

function AppRoutes() {
    const { loading, user } = useAuth();
    const { isInstalled } = usePWA();
    const [showWelcome, setShowWelcome] = useState(false);
    const [isPageReady, setIsPageReady] = useState(false);
    const location = useLocation();
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        console.log('📱 PWA статус:', { isInstalled });
    }, [isInstalled]);

    useEffect(() => {
        const initializeWelcome = async () => {
            if (!loading && isMounted.current) {
                const hasSeenWelcome = localStorage.getItem('has_seen_welcome');

                if (!hasSeenWelcome && !user) {
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    if (isMounted.current) {
                        setIsPageReady(true);
                        setShowWelcome(true);
                    }
                } else if (isMounted.current) {
                    setIsPageReady(true);
                }
            }
        };

        initializeWelcome();
    }, [loading, user]);

    const handleWelcomeComplete = () => {
        setShowWelcome(false);
        localStorage.setItem('has_seen_welcome', 'true');
    };

    const shouldShowBottomNav = useCallback(() => {
        const hidePaths = ['/checkout', '/delivery-selection', '/profile/edit'];
        return !hidePaths.includes(location.pathname);
    }, [location.pathname]);

    // Показываем лоадер только во время начальной загрузки
    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner" />
                <p>Загрузка приложения...</p>
            </div>
        );
    }

    return (
        <div className="app-mobile">
            {showWelcome && isPageReady && <WelcomeDialog onComplete={handleWelcomeComplete} />}
            <BrowserInstructionModal />
            <PWAInstallBanner />
            <Navbar />
            <main className="main-content">
                <AliveScope>
                    <Routes>
                        {/* Публичные маршруты */}
                        <Route path="/login" element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        } />
                        <Route path="/register" element={
                            <PublicRoute>
                                <Register />
                            </PublicRoute>
                        } />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/reset-password" element={<ResetPassword />} />

                        {/* Главная страница */}
                        <Route path="/" element={
                            <KeepAlive name="Home" saveScrollPosition="screen">
                                <Home />
                            </KeepAlive>
                        } />

                        {/* Каталог и товары */}
                        <Route path="/category/:categoryId?" element={<CategoryPage />} />
                        <Route path="/products" element={
                            <KeepAlive name="Products" saveScrollPosition="screen">
                                <Products />
                            </KeepAlive>
                        } />
                        <Route path="/search" element={<SearchPage />} />
                        <Route path="/product/:id" element={<ProductDetail />} />

                        {/* Защищенные маршруты */}
                        <Route path="/profile" element={
                            <ProtectedRoute>
                                <KeepAlive name="Profile" saveScrollPosition="screen">
                                    <Profile />
                                </KeepAlive>
                            </ProtectedRoute>
                        } />

                        <Route path="/profile/edit" element={
                            <ProtectedRoute>
                                <ProfileEdit />
                            </ProtectedRoute>
                        } />

                        <Route path="/cart" element={
                            <ProtectedRoute>
                                <KeepAlive name="Cart" saveScrollPosition="screen">
                                    <Cart />
                                </KeepAlive>
                            </ProtectedRoute>
                        } />

                        <Route path="/orders" element={
                            <ProtectedRoute>
                                <KeepAlive name="Orders" saveScrollPosition="screen">
                                    <Orders />
                                </KeepAlive>
                            </ProtectedRoute>
                        } />

                        <Route path="/wishlist" element={
                            <ProtectedRoute>
                                <KeepAlive name="Wishlist" saveScrollPosition="screen">
                                    <Wishlist />
                                </KeepAlive>
                            </ProtectedRoute>
                        } />

                        <Route path="/checkout" element={
                            <ProtectedRoute>
                                <Checkout />
                            </ProtectedRoute>
                        } />

                        <Route path="/delivery-selection" element={
                            <ProtectedRoute>
                                <DeliverySelection />
                            </ProtectedRoute>
                        } />

                        {/* 404 - не найдено */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </AliveScope>
            </main>
            {shouldShowBottomNav() && <BottomNavigation />}
        </div>
    );
}

// ========== ГЛАВНЫЙ КОМПОНЕНТ ==========

function App() {
    return (
        <Router>
            <AppInitializer>
                <AuthProvider>
                    <CartProvider>
                        <SessionRefresher />
                        <VisitorTracker />
                        <SearchQueueInitializer />
                        <ScrollRestorationSetup />
                        <DeliveryConfigInitializer />


                        <Toaster
                            position="top-center"
                            toastOptions={{
                                duration: 2000,
                                style: {
                                    background: '#363636',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    fontSize: '14px',
                                    padding: '12px 16px',
                                },
                                success: {
                                    duration: 2000,
                                },
                                error: {
                                    duration: 3000,
                                },
                            }}
                        />

                        <AppRoutes />
                    </CartProvider>
                </AuthProvider>
            </AppInitializer>
        </Router>
    );
}

export default App;